import { useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Target, Lock, Activity, Gift, Zap, Radio, TrendingUp, Trophy, X } from "lucide-react";

const preMatchSteps = [
  { icon: Target, title: "Predict", desc: "Guess the total runs before the match starts" },
  { icon: Lock, title: "Lock", desc: "Your prediction locks when the match begins" },
  { icon: Activity, title: "Track", desc: "Watch your prediction vs actual runs live" },
  { icon: Gift, title: "Win", desc: "Closest predictions earn points & rewards" },
];

const liveSteps = [
  { icon: Zap, title: "Join a live match", desc: "Open any match that's in progress" },
  { icon: Radio, title: "Answer checkpoint questions", desc: "Short questions appear during the match (e.g. score at end of an over)" },
  { icon: TrendingUp, title: "Score as it happens", desc: "Points land automatically after each checkpoint resolves" },
  { icon: Trophy, title: "Climb the live leaderboard", desc: "A separate live leaderboard updates after every checkpoint" },
];

const Step = ({ icon: Icon, title, desc }) => (
  <div className="flex items-start gap-4">
    <div
      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
      style={{ background: "linear-gradient(135deg, #01ff9a, #acffe5)" }}
    >
      <Icon className="w-5 h-5 text-[#0f0f0f]" />
    </div>
    <div>
      <p className="text-white font-medium">{title}</p>
      <p className="text-sm text-white/50">{desc}</p>
    </div>
  </div>
);

const ALLOWED_SECTIONS = new Set(["pre-match", "live-predictions"]);

const HowItWorksModal = ({ isOpen, onClose, scrollToSection }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!isOpen || !scrollToSection) return;
    if (!ALLOWED_SECTIONS.has(scrollToSection)) return;
    // Wait one tick so the slide-up animation has started before scrolling.
    const id = setTimeout(() => {
      const el = containerRef.current?.querySelector(
        `#${CSS.escape(scrollToSection)}`,
      );
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 250);
    return () => clearTimeout(id);
  }, [isOpen, scrollToSection]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-end justify-center"
        style={{ background: "rgba(0,0,0,0.7)" }}
        onClick={onClose}
      >
        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="w-full max-w-md rounded-t-3xl p-6 pb-10 max-h-[85vh] overflow-y-auto"
          style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.1)" }}
          onClick={(e) => e.stopPropagation()}
          ref={containerRef}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-white">How it works</h2>
            <button onClick={onClose} className="p-1 rounded-full hover:bg-white/10">
              <X className="w-5 h-5 text-white/60" />
            </button>
          </div>

          {/* Pre-match section */}
          <div id="pre-match" className="space-y-4">
            {preMatchSteps.map((step, i) => (
              <Step key={i} {...step} />
            ))}
          </div>

          {/* Live Predictions section */}
          <div id="live-predictions" className="mt-8 pt-6 border-t border-white/10">
            <div className="flex items-center gap-2 mb-4">
              <h3 className="text-base font-semibold text-white">Live Predictions</h3>
              <span
                className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                style={{ background: "rgba(1,255,154,0.15)", color: "#01ff9a" }}
              >
                New
              </span>
            </div>
            <div className="space-y-4">
              {liveSteps.map((step, i) => (
                <Step key={i} {...step} />
              ))}
            </div>
          </div>

          <button
            onClick={onClose}
            className="w-full mt-6 py-3 rounded-xl font-semibold"
            style={{ background: "linear-gradient(90deg, #01ff9a, #acffe5)", color: "#0f0f0f" }}
          >
            Got it!
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default HowItWorksModal;
